import {
  isDateIntervalCorrect,
  isFieldWithValue,
  isSunday,
  ReccurenceDay,
} from "domains/Request/component/shared/utils";
import {
  AddRequestInput,
  Address,
  Client,
  EActionType,
  EProductType,
  EReccurenceType,
  ERequestType,
  EVehiclesInput,
  EVehiculeType,
  Product,
  ReccurenceOptionsInput,
  Request,
  TemplateAxeInput,
} from "generated/graphql";
import React from "react";

export type AddMADRequestInput = AddRequestInput & { _id?: string };
export interface IProductPick extends Product {
  pickup?: number;
  refPickupDelivery?: string;
}
export interface IReccurenceOptions {
  each: Number;
  days?: Array<ReccurenceDay>;
  value?: Number;
}

export interface IReccurenceQuoteObject {
  value: EReccurenceType;
  options: ReccurenceOptionsInput;
}

export enum INationalTransportAction {
  Pickup = "PICKUP",
  Delivery = "DELIVERY",
  Pickup_Delivery = "PICKUP_DELIVERY",
}

export interface ItemAtPoint {
  product: IProductPick;
  counter: number;
  remainder?: number;
  pickup?: number[];
}

export interface InationalTransportPoint {
  address: Address;
  action: EActionType;
  itemsToPick?: ItemAtPoint[];
  itemsToDelivered?: ItemAtPoint[];
}
export interface InationalTransportObject {
  clientId: string;
  type: ERequestType;
  vehicles: EVehiclesInput[];
  startDate: string;
  endDate: string;
  slotStart: string;
  slotEnd: string;
  productType: EProductType[];
  additionnalInfos: string;
  reccurenceType: EReccurenceType;
  reccurenceOptions: ReccurenceOptionsInput;
  stops: InationalTransportPoint[];
  _id?: string;
}

export interface ITourPoint {
  address: Address;
  action?: EActionType;
  itemsToPick?: ItemAtPoint[];
  itemsToDelivered?: ItemAtPoint[];
}

export interface ITourObject extends AddRequestInput {
  templateAxes: ITourTemplateAxe[];
  client: Client;
  _id?: string;
  stops: ITourPoint[];
  type: ERequestType;
  vehicles: EVehiclesInput[];
  startDate: string;
  endDate: string;
  slotStart: string;
  slotEnd: string;
  productType: EProductType[];
  additionnalInfos: string;
  reccurenceType: EReccurenceType;
  reccurenceOptions: ReccurenceOptionsInput;
  templateStops: ITourStop[];
  clientId: string;
}

export interface ITourStop {
  address: Address;
  action?: EActionType;
}
export interface ITourTemplateAxe extends TemplateAxeInput {
  uid?: string;
  templateStops: ITourStop[];
}

export interface ISamaPapserRequestQuote {
  requestQuote: AddMADRequestInput;
  setRequestQuote: React.Dispatch<React.SetStateAction<AddMADRequestInput>>;

  requestQuoteNT: InationalTransportObject;
  setRequestQuoteNT: React.Dispatch<React.SetStateAction<InationalTransportObject>>;

  requestQuoteTour: ITourObject;
  setRequestQuoteTour: React.Dispatch<React.SetStateAction<ITourObject>>;

  requestQuoteDetails: Request;
  setRequestQuoteDetails: React.Dispatch<React.SetStateAction<Request>>;

  details: string;
  setDetails: React.Dispatch<React.SetStateAction<String>>;

  step: number;
  setStep: React.Dispatch<React.SetStateAction<Number>>;

  truckType: EVehiculeType;
  setTruckType: React.Dispatch<React.SetStateAction<EVehiculeType>>;

  isEditedNT: boolean;
  setIsEditedNT: React.Dispatch<React.SetStateAction<boolean>>;
  isEditedTour: boolean;
  setIsEditedTour: React.Dispatch<React.SetStateAction<boolean>>;

  fieldError: boolean;
  setFieldError: React.Dispatch<React.SetStateAction<boolean>>;

  nextScreen: boolean;
  setNextScreen: React.Dispatch<React.SetStateAction<boolean>>;

  newRequestBadgeTimeOut: boolean;
  setNewRequestBadgeTimeOut: React.Dispatch<React.SetStateAction<boolean>>;
}

export const NewRequestQuoteContext = React.createContext<ISamaPapserRequestQuote>(null);

export const checkReccurenceIntervalDate = (requestQuote: AddRequestInput) =>
  (requestQuote?.reccurenceType === EReccurenceType.DoesNotRepeat &&
    isFieldWithValue(requestQuote?.startDate) &&
    !isSunday(requestQuote?.startDate)) ||
  (requestQuote?.reccurenceType !== EReccurenceType.DoesNotRepeat &&
    isDateIntervalCorrect(requestQuote?.startDate, requestQuote?.endDate));

export const isVehicleTruckChecked = (vehicles: EVehiclesInput[]) =>
  vehicles && vehicles?.findIndex((v) => v.vehicleType.includes("TRUCK")) !== -1;

export const formatVehiclesSelected = (vehicles: EVehiclesInput[], truckType: EVehiculeType) =>
  vehicles?.map((v) =>
    v.vehicleType.includes("TRUCK")
      ? {
          vehicleType: truckType,
          numberOfVehicule: v.numberOfVehicule,
        }
      : v
  );

export const isSameItem = (firstProduct: IProductPick, secondProduct: IProductPick) =>
  firstProduct?._id === secondProduct?._id ||
  (isFieldWithValue(firstProduct?.refPickupDelivery) &&
    isFieldWithValue(secondProduct?.refPickupDelivery) &&
    firstProduct?.refPickupDelivery === secondProduct?.refPickupDelivery);

const NewRequestQuoteProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [requestQuote, setRequestQuote] = React.useState<AddMADRequestInput>(null);
  const [requestQuoteNT, setRequestQuoteNT] = React.useState<InationalTransportObject>(null);
  const [requestQuoteDetails, setRequestQuoteDetails] = React.useState<Request>(null);
  const [details, setDetails] = React.useState(null);
  const [step, setStep] = React.useState(null);
  const [fieldError, setFieldError] = React.useState(null);
  const [truckType, setTruckType] = React.useState<EVehiculeType>(null);
  const [nextScreen, setNextScreen] = React.useState(false);
  const [isEditedNT, setIsEditedNT] = React.useState(false);
  const [newRequestBadgeTimeOut, setNewRequestBadgeTimeOut] = React.useState(false);
  const [requestQuoteTour, setRequestQuoteTour] = React.useState<ITourObject>(null);
  const [isEditedTour, setIsEditedTour] = React.useState(false);

  return (
    <NewRequestQuoteContext.Provider
      value={{
        requestQuote,
        setRequestQuote,
        requestQuoteNT,
        setRequestQuoteNT,
        requestQuoteTour,
        setRequestQuoteTour,
        fieldError,
        setFieldError,
        isEditedNT,
        setIsEditedNT,
        truckType,
        setTruckType,
        nextScreen,
        setNextScreen,
        requestQuoteDetails,
        setRequestQuoteDetails,
        details,
        setDetails,
        step,
        setStep,
        newRequestBadgeTimeOut,
        setNewRequestBadgeTimeOut,
        isEditedTour,
        setIsEditedTour,
      }}
    >
      {children}
    </NewRequestQuoteContext.Provider>
  );
};

export default NewRequestQuoteProvider;
